import { createSelector, createSlice } from "@reduxjs/toolkit";
import { generateClient } from 'aws-amplify/api';
import moment from 'moment';
import { emailDailyReport, exportDailyReport } from '../graphql/mutations';
import { dailyReportByCustomerId, dailyReportByForemanId, searchDailyReports } from "../graphql/customQueries";
import service from "../service";
import { selectUser } from "./auth";
import { popPreviousToken, pushPreviousToken, reset, resetPagination } from "./globalActions";

const client = generateClient();

const moduleName = "dailyreport";

const initialState = {
    loading: false,
    list: [],
    nextToken: null,
    previousTokens: [],
    activeReport: null
};

const userSlice = createSlice({
    name: moduleName,
    initialState,
    extraReducers: (builder) => {
        builder.addCase(reset, () => initialState);
        builder.addCase(resetPagination, (state) => {
            state.nextToken = null;
            state.previousTokens = [];
        });
        builder.addCase(popPreviousToken, (state) => {
            state.previousTokens.pop();
        });
        builder.addCase(pushPreviousToken, (state, action) => {
            state.previousTokens.push(action.payload);
        });
    },
    reducers: {
        setLoading: (state, action) => {
            state.loading = action.payload;
        },
        setActive: (state, action) => {
            state.activeReport = action.payload;
        },
        setDailyReports: (state, action) => {
            state.list = action.payload.items;
            state.nextToken = action.payload.nextToken;
        },
        onCreateDailyReport: (state, action) => {
            let idx = state.list.findIndex((c) => c.id === action.payload?.id);
            idx >= 0 ? (state.list[idx] = action.payload) : state.list.push(action.payload);
        },
        onUpdateDailyReport: (state, action) => {
            let idx = state.list.findIndex((c) => c.id === action.payload?.id);
            idx >= 0 ? (state.list[idx] = action.payload) : state.list.push(action.payload);
        },
        onDeleteDailyReport: (state, action) => {
            let dailyreportIndex = state.list.findIndex((c) => c.id === action.payload.id);
            state.list = [...state.list.slice(0, dailyreportIndex), ...state.list.slice(dailyreportIndex + 1)];
        },
    },
});

export const { setLoading, setDailyReports, onCreateDailyReport, onUpdateDailyReport, onDeleteDailyReport, setActive } = userSlice.actions;
export default userSlice.reducer;

export const selectState = (state) => state[moduleName];
export const selectLoading = createSelector(selectState, (state) => state.loading);
export const selectPagination = createSelector(selectState, (state) => ({ nextToken: state.nextToken, previousTokens: state.previousTokens }));
export const selectDailyReports = createSelector(selectState, (state) => state.list);
export const selectActiveReport = createSelector(selectState, (state) => state.activeReport);
export const selectDailyReportById = createSelector([selectDailyReports, (state, id) => id], (reports, id) => {
    if (!id) return null;
    return reports.find((report) => report.id === id);
});

export const fetchDailyReport = (id) => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        const report = await service.fetchDailyReport(id);
        if (!report) throw new Error("Report does not exist");
        dispatch(onUpdateDailyReport(report));
        dispatch(setActive(report));
        return report;
    } finally {
        dispatch(setLoading(false));
    }
};

export const fetchDailyReports =
    (locationId, startDate, endDate, nextToken, storeNextToken = true) =>
        async (dispatch, getState) => {
            try {
                dispatch(setLoading(true));
                if (storeNextToken) dispatch(pushPreviousToken(nextToken));
                else dispatch(popPreviousToken());

                const reports = await service.fetchDailyReports(locationId, startDate, endDate, undefined, nextToken);
                dispatch(setDailyReports(reports));
            } finally {
                dispatch(setLoading(false));
            }
        };

export const findDailyReports =
    (locationId, search, limit = 12, nextToken, storeNextToken = true) =>
        async (dispatch, getState) => {
            try {
                dispatch(setLoading(true));
                if (storeNextToken) dispatch(pushPreviousToken(nextToken));
                else dispatch(popPreviousToken());

                const { data } = await client.graphql({
                    query: searchDailyReports,
                    variables: {
                        nextToken,
                        limit,
                        filter: {
                            locationId: { eq: locationId },
                            or: [
                                { summary: { wildcard: `*${search}*`, } },
                                { address: { wildcard: `*${search}*` } },
                                { customerName: { wildcard: `*${search}*` } },
                            ]
                        },
                        sort: [{ field: "submissionDate", direction: 'asc' }]
                    }
                })
                dispatch(setDailyReports(data.searchDailyReports));
                return data.searchDailyReports.items;
            } finally {
                dispatch(setLoading(false));
            }
        };

export const fetchDailyReportByUserId = () => async (dispatch, getState) => {
    try {
        dispatch(setLoading(true));
        const { username } = selectUser(getState());
        const items = await service.dailyReportByUserId(username);
        dispatch(setDailyReports({ items }));
    } finally {
        dispatch(setLoading(false));
    }
};

export const fetchDailyReportByCustomerId = (customerId, startDate = moment(), endDate = moment(), limit, nextToken, storeNextToken = true) => async (dispatch, getState) => {
    try {
        dispatch(setLoading(true));
        if (storeNextToken) dispatch(pushPreviousToken(nextToken));
        else dispatch(popPreviousToken());

        const { data } = await client.graphql({
            query: dailyReportByCustomerId,
            variables: {
                customerId,
                nextToken,
                limit,
                filter: {
                    submissionDate: {
                        between: [moment(startDate).startOf('day'), moment(endDate).endOf('day')]
                    }
                }
            }
        })
        dispatch(setDailyReports(data.dailyReportByCustomerId));
        return data.dailyReportByCustomerId.items;
    } finally {
        dispatch(setLoading(false));
    }
}

export const fetchDailyReportByCrewMemberId = (foremanId, startDate = moment(), endDate = moment(), limit, nextToken, storeNextToken = true) => async (dispatch, getState) => {
    try {
        dispatch(setLoading(true));
        if (storeNextToken) dispatch(pushPreviousToken(nextToken));
        else dispatch(popPreviousToken());

        const { data } = await client.graphql({
            query: dailyReportByForemanId,
            variables: {
                foremanId,
                nextToken,
                limit,
                submissionDate: {
                    between: [moment(startDate).startOf('day'), moment(endDate).endOf('day')]
                }
            }
        })
        dispatch(setDailyReports(data.dailyReportByForemanId));
        return data.dailyReportByForemanId.items;
    } finally {
        dispatch(setLoading(false));
    }
}

export const createDailyReport = (dailyreport) => async (dispatch) => {
    const createdDailyReport = await service.createDailyReport(dailyreport);
    dispatch(onCreateDailyReport(createdDailyReport));
};

export const updateDailyReport = (dailyreport) => async (dispatch) => {
    const updatedDailyReport = await service.updateDailyReport(dailyreport);
    dispatch(onUpdateDailyReport(updatedDailyReport));
};

export const deleteDailyReport = (dailyreport) => async (dispatch) => {
    const deletedDailyReport = await service.deleteDailyReport(dailyreport);
    dispatch(onDeleteDailyReport(deletedDailyReport));
};
export const exportDailyReportPDF = (params) => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        const { data } = await client.graphql({
            query: exportDailyReport,
            variables: {
                input: params,
            },
        });
        let url = data.exportDailyReport.url;
        if (!url) throw new Error("Failed to export report");
        return url;
    } finally {
        dispatch(setLoading(false));
    }
};

export const sendDailyReportByEmail = (dailyReportId, to, cc, bcc) => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        const { data: { emailDailyReport: response } } = await client.graphql({
            query: emailDailyReport,
            variables: {
                input: {
                    dailyReportId,
                    to,
                    cc,
                    bcc
                },
            },
        });
        if (!response.status) throw new Error(response.message);
        return response;
    } finally {
        dispatch(setLoading(false));
    }
};
